import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
    Box,
    Button,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CustomizedSnackbars from '../material-snackbars';
import DialogConfirmation from '../dialog-confirmation';
import WebhookResponseMappingDialog from './mapping-dialog';
import WebhookDiscoverPathsDialog from './discover-paths-dialog';
import {
    getWebhookResponseMappings,
    createWebhookResponseMapping,
    updateWebhookResponseMapping,
    deleteWebhookResponseMapping,
} from '../../services/webhook.service';

// Aba "Capturar dados do retorno" (S3.1): lista mappings, permite CRUD + auto-discovery.
// Reusavel: recebe webhookId por prop e cuida do resto sozinho. Renderizado dentro de
// uma Accordion no webhook-edit — mas nada impede reuso em outro contexto.
class WebhookResponseMappings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            mappings: [],
            dialogOpen: false,
            dialogMapping: null, // null = create; objeto = edit
            discoverOpen: false,
            deleteConfirmOpen: false,
            deleteTarget: null,
            deleting: false,
            notification: { open: false, variant: 'success', message: '' },
        };
        this.reload = this.reload.bind(this);
        this.openCreate = this.openCreate.bind(this);
        this.openEdit = this.openEdit.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
        this.doDelete = this.doDelete.bind(this);
        this.openDiscover = this.openDiscover.bind(this);
    }

    componentDidMount() {
        if (this.props.webhookId) this.reload();
    }

    componentDidUpdate(prevProps) {
        if (this.props.webhookId && this.props.webhookId !== prevProps.webhookId) {
            this.reload();
        }
    }

    async reload() {
        if (!this.props.webhookId) return;
        this.setState({ loading: true });
        try {
            const res = await getWebhookResponseMappings(this.props.webhookId);
            if (res.success && Array.isArray(res.data)) {
                this.setState({ mappings: res.data });
            } else {
                this.setState({ mappings: [] });
                if (res.errors?.length) {
                    this.showNotification('error', res.errors[0]);
                }
            }
        } catch (err) {
            this.showNotification('error', err.message);
        } finally {
            this.setState({ loading: false });
        }
    }

    openCreate() { this.setState({ dialogOpen: true, dialogMapping: null }); }
    openEdit(mapping) { this.setState({ dialogOpen: true, dialogMapping: mapping }); }
    openDiscover() { this.setState({ discoverOpen: true }); }

    async handleSubmit(payload) {
        const { webhookId, intl } = this.props;
        const { dialogMapping } = this.state;
        try {
            const res = dialogMapping
                ? await updateWebhookResponseMapping(webhookId, dialogMapping.id, payload)
                : await createWebhookResponseMapping(webhookId, payload);
            if (res.success) {
                this.showNotification('success', intl.formatMessage({
                    id: dialogMapping ? 'webhook.response.mapping.updated' : 'webhook.response.mapping.created'
                }));
                this.setState({ dialogOpen: false, dialogMapping: null });
                this.reload();
            } else {
                this.showNotification('error', res.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
            }
        } catch (err) {
            this.showNotification('error', err.message);
        }
    }

    confirmDelete(mapping) {
        this.setState({ deleteConfirmOpen: true, deleteTarget: mapping });
    }

    async doDelete() {
        const { webhookId, intl } = this.props;
        const { deleteTarget } = this.state;
        if (!deleteTarget) return;
        this.setState({ deleting: true });
        try {
            const res = await deleteWebhookResponseMapping(webhookId, deleteTarget.id);
            if (res.success) {
                this.showNotification('success', intl.formatMessage({ id: 'webhook.response.mapping.deleted' }));
                this.setState({ deleteConfirmOpen: false, deleteTarget: null });
                this.reload();
            } else {
                this.showNotification('error', res.errors?.[0] || intl.formatMessage({ id: 'process.error' }));
            }
        } catch (err) {
            this.showNotification('error', err.message);
        } finally {
            this.setState({ deleting: false });
        }
    }

    showNotification(variant, message) {
        this.setState({ notification: { open: true, variant, message } });
    }
    closeNotification = () => this.setState(prev => ({ notification: { ...prev.notification, open: false } }));

    renderEmptyState() {
        const { intl } = this.props;
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    padding: 4,
                    border: '1px dashed #e0e0e0',
                    borderRadius: 2,
                }}
            >
                <SearchIcon sx={{ fontSize: 56, color: '#bdbdbd' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                    {intl.formatMessage({ id: 'webhook.response.mapping.empty.title' })}
                </Typography>
                <Typography variant="body2" sx={{ color: '#888', mb: 2, maxWidth: 500, margin: '4px auto 16px' }}>
                    {intl.formatMessage({ id: 'webhook.response.mapping.empty.desc' })}
                </Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={this.openCreate} style={{ marginRight: 8 }}>
                    {intl.formatMessage({ id: 'webhook.response.mapping.add' })}
                </Button>
                <Button variant="outlined" color="primary" startIcon={<SearchIcon />} onClick={this.openDiscover}>
                    {intl.formatMessage({ id: 'webhook.response.discover.suggest' })}
                </Button>
            </Box>
        );
    }

    render() {
        const { intl, webhookId, providers } = this.props;
        const { loading, mappings, dialogOpen, dialogMapping, discoverOpen, deleteConfirmOpen, deleting, notification } = this.state;

        if (!webhookId) {
            return (
                <Box sx={{ padding: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        {intl.formatMessage({ id: 'webhook.response.mapping.save.first' })}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {intl.formatMessage({ id: 'webhook.response.mapping.section.desc' })}
                    </Typography>
                    {mappings.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="outlined" color="primary" startIcon={<SearchIcon />} onClick={this.openDiscover}>
                                {intl.formatMessage({ id: 'webhook.response.discover.suggest' })}
                            </Button>
                            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={this.openCreate}>
                                {intl.formatMessage({ id: 'webhook.response.mapping.add' })}
                            </Button>
                        </Box>
                    )}
                </Box>

                {loading && (
                    <>
                        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} animation="wave" />
                        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} animation="wave" />
                        <Skeleton variant="rectangular" height={40} animation="wave" />
                    </>
                )}

                {!loading && mappings.length === 0 && this.renderEmptyState()}

                {!loading && mappings.length > 0 && (
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{intl.formatMessage({ id: 'webhook.response.mapping.key' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'webhook.response.mapping.jsonpath' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'webhook.response.mapping.entitytype' })}</TableCell>
                                    <TableCell>{intl.formatMessage({ id: 'webhook.response.mapping.active' })}</TableCell>
                                    <TableCell align="right">{intl.formatMessage({ id: 'actions' })}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mappings.map((m) => (
                                    <TableRow key={m.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                                                {`{{external.${m.externalkeyname}}}`}
                                            </Typography>
                                            {m.description && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {m.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#1976d2' }}>
                                                {m.jsonpath}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={m.externalentitytype} variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {m.active
                                                ? <Chip size="small" label={intl.formatMessage({ id: 'yes' })} color="success" />
                                                : <Chip size="small" label={intl.formatMessage({ id: 'no' })} />}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={intl.formatMessage({ id: 'edit' })}>
                                                <IconButton size="small" onClick={() => this.openEdit(m)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={intl.formatMessage({ id: 'delete' })}>
                                                <IconButton size="small" color="error" onClick={() => this.confirmDelete(m)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <WebhookResponseMappingDialog
                    open={dialogOpen}
                    mapping={dialogMapping}
                    providers={providers}
                    onClose={() => this.setState({ dialogOpen: false, dialogMapping: null })}
                    onSubmit={this.handleSubmit}
                />

                {discoverOpen && (
                    <WebhookDiscoverPathsDialog
                        open={discoverOpen}
                        webhookId={webhookId}
                        onClose={() => this.setState({ discoverOpen: false })}
                        onCreated={this.reload}
                    />
                )}

                <DialogConfirmation
                    msgButton={intl.formatMessage({ id: 'confirm' })}
                    msg={intl.formatMessage({ id: 'webhook.response.mapping.delete.confirm' })}
                    msgDisagree={intl.formatMessage({ id: 'cancel' })}
                    msgAgree={intl.formatMessage({ id: 'yes' })}
                    handleConfirmation={this.doDelete}
                    classes={{}}
                    isOpen={deleteConfirmOpen}
                    disabled={deleting}
                    itemName={this.state.deleteTarget ? `{{external.${this.state.deleteTarget.externalkeyname}}}` : ''}
                    handleClose={() => this.setState({ deleteConfirmOpen: false, deleteTarget: null })}
                />

                <CustomizedSnackbars
                    variant={notification.variant}
                    message={notification.message}
                    isOpen={notification.open}
                    toClose={this.closeNotification}
                />
            </Box>
        );
    }
}

WebhookResponseMappings.propTypes = {
    webhookId: PropTypes.number,
    providers: PropTypes.array,
    intl: PropTypes.object.isRequired,
};

WebhookResponseMappings.defaultProps = {
    providers: [],
};

export default injectIntl(WebhookResponseMappings);
